import axios from 'axios';
import queryString, { type StringifyOptions } from 'query-string';
import Cookies from 'js-cookie';
import c, { ROLES } from 'utils/constants';

export const QUERY_OPTIONS = { arrayFormat: 'index' } as StringifyOptions;

const instance = axios.create({
  baseURL: import.meta.env.VITE_APP_END_POINT,
  timeout: 30000,
  headers: {
    Accepts: 'application/json',
    'Content-type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Credentials': true,
  },
});

instance.interceptors.request.use((config) => {
  const token = Cookies.get('_token');

  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

interface API_OPTIONS {
  url: string;
  payload?: any;
  params?: any;
  options?: any;
  transformer?: (f: any) => any;
}

class Api {
  constructor(args: any) {
    this.request = instance;

    this.onError = args?.onError;

    this.getError = ({ response, request, message }) => {
      if (response) return response;
      if (request) return request;
      if (message) return message;

      return null;
    };

    this.result = {};
  }

  getError = (err: any) => err;
  onError = (err: any) => err;
  request = instance;
  result = {};

  event = async ({ url, params }: API_OPTIONS) => {
    try {
      const token = Cookies.get('_token');

      const eventSource = new EventSource(
        `${import.meta.env.VITE_APP_END_POINT}${url}?${queryString.stringify(
          {
            ...params,
            token,
          },
          QUERY_OPTIONS,
        )}`,
      );

      return eventSource;
    } catch (err) {
      const error = this.getError(err);

      this.onError?.(error);

      throw error;
    }
  };

  get = async ({ url, params, transformer, options }: API_OPTIONS) => {
    try {
      const res = await this.request.get(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        options,
      );

      return transformer ? transformer(res?.data) : res?.data;
    } catch (err) {
      const error = this.getError(err);

      this.onError?.(error);

      throw error;
    }
  };

  post = async ({
    url,
    payload,
    params,
    transformer,
    options,
  }: API_OPTIONS) => {
    try {
      const res = await this.request.post(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        payload,
        options,
      );

      return transformer ? transformer(res?.data) : res?.data;
    } catch (err) {
      const error = this.getError(err);

      this.onError?.(error);

      throw error;
    }
  };

  patch = async ({
    url,
    payload,
    params,
    transformer,
    options,
  }: API_OPTIONS) => {
    try {
      const res = await this.request.patch(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        payload,
        options,
      );

      return transformer ? transformer(res?.data) : res?.data;
    } catch (err) {
      const error = this.getError(err);

      this.onError?.(error);

      throw error;
    }
  };

  put = async ({ url, payload, params, transformer, options }: API_OPTIONS) => {
    try {
      const res = await this.request.put(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        payload,
        options,
      );

      return transformer ? transformer(res?.data) : res?.data;
    } catch (err) {
      const error = this.getError(err);

      this.onError?.(error);

      throw error;
    }
  };

  delete = async ({
    url,
    payload,
    params,
    transformer,
    options,
  }: API_OPTIONS) => {
    try {
      const res = await this.request.delete(
        `${url}?${queryString.stringify(params, QUERY_OPTIONS)}`,
        { data: payload, ...options },
      );

      return transformer ? transformer(res?.data) : res?.data;
    } catch (err) {
      const error = this.getError(err);

      this.onError?.(error);

      throw error;
    }
  };
}

export async function withAcl(acl: any) {
  const role = Cookies.get('_role') as ROLES | undefined;

  if (!role) return {};

  if (!acl[c.ROLES[role]]) return {};

  return acl[c.ROLES[role]];
}

export default Api;
